import React, { useState } from "react";

import { css, keyframes } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useGlobalNavUserProfileClickActions } from "@/components/global-nav/user-profile-new/src/user-profile";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { LogoV2 as Logo } from "@/libraries/wpl/foundations/src";

import AutoCompleteSearchBar from "../auto-complete-search-bar";
import { useMobileNavDisplay } from "../hooks/use-mobile-nav-display";

import { Divider } from "./icons/divider";
import {
    MobileNavLinks,
    MobileNavLinksContainerInfo
} from "./mobile-nav-links-container";
import MobileLoggedInUseDropdown from "./mobile-nav-logged-in-dropdown";
import { MobileNavTop } from "./mobile-nav-top";
import { MobileProductPageNavDropdowns } from "./mobile-product-page-nav-dropdowns";

const containerCss = css({
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: token("space.150")
});

const shownInnerMobileDropdownMenuStyle = css({
    display: "block",
    margin: "8px 16px"
});

const hiddenInnerMobileDropdownMenuStyle = css({
    display: "none",
    margin: "8px 16px"
});

const containerClosedCss = css({
    padding: "8px",
    [media.above.sm]: {
        padding: "0"
    }
});

const containerOpenCss = css({
    [media.below.sm]: {
        height: "100vh",
        width: "100%",
        overflow: "auto",
        position: "fixed",
        insetBlockStart: "0",
        padding: "8px 24px 24px 24px",
        zIndex: "5"
    },
    padding: `0`
});

const navBarClosedCss = css({
    paddingInline: token("space.200"),
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const navBarOpenCss = css({
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const menuContentStyle = css({
    display: "none",
    width: "auto",

    [media.above.sm]: {
        display: "none"
    }
});

const fadeIn = keyframes({
    from: {
        opacity: "0"
    },
    to: {
        opacity: "1"
    }
});

const slideInLeft = keyframes({
    from: {
        transform: "translateX(-100%)",
        opacity: "0"
    },
    to: {
        transform: "translateX(0%)",
        opacity: "1"
    }
});

const slideInRight = keyframes({
    from: {
        transform: "translateX(100%)",
        opacity: "0"
    },
    to: {
        transform: "translateX(0%)",
        opacity: "1"
    }
});

const fadeInCss = css({
    opacity: "0",
    animation: `0.4s ease-in ${fadeIn} 200ms forwards`
});

const slideInLeftCss = css({
    opacity: "0",
    animation: `0.5s ${slideInLeft} forwards`
});

const slideInRightCss = css({
    opacity: "0",
    animation: `0.5s ${slideInRight} forwards`
});

export type DropdownType = "default" | "search" | "why-bitbucket";

export type MobileGlobalNavProps = {
    productKey: ProductKeyTypes;
    mobileLinksSections: MobileNavLinksContainerInfo[];
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    isIncludeCTA?: boolean;
    ctaText?: string;
    ctaUrl?: string;
    ctaOnClick?: (e: React.MouseEvent) => void;
    isIncludeAtlassianLogo?: boolean;
    isShowTryNowLink?: boolean;
    additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
    isShowSearchButton?: boolean;
    isShowAppSwitcher?: boolean;
    isShowTabletDropdown?: boolean;
    navType?: "globalNav" | "productNav";
};

export type MobileGlobalNavMenuProps = {
    isDisplayLoggedInUseDropdown: boolean;
};

export type AdditionalMenuPageObjects = {
    menuPageKey: string;
    menuPageObjects: JSX.Element;
};

export type addedMenuPagesObjectType = {
    [key: string]: JSX.Element;
};

const MobileNavProductPageDropdownMenu = ({
    isUserLoggedIn,
    menuPage = "default",
    firstOpen,
    handleMenuPage,
    mobileLinksSections,
    productKey,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = true,
    additionalMenuPageObjectsArray = [],
    isShowSearchButton = true
}: {
    readonly isUserLoggedIn: boolean;
    readonly menuPage: DropdownType;
    readonly firstOpen: boolean;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    readonly productKey: ProductKeyTypes;
    readonly isIncludeCTA?: boolean;
    readonly ctaText?: string;
    readonly ctaUrl?: string;
    readonly ctaOnClick?: (e: React.MouseEvent) => void;
    readonly isIncludeAtlassianLogo?: boolean;
    readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
    readonly isShowSearchButton?: boolean;
}) => {
    const MobileNavLinksMenuPage: JSX.Element = (
        <MobileNavLinks
            isLoggedIn={isUserLoggedIn}
            handleMenuPage={handleMenuPage}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
            isShowSearchButton={isShowSearchButton}
        />
    );

    const menuPages: { pageKey: string; content: JSX.Element }[] = [
        { pageKey: "default", content: MobileNavLinksMenuPage }
    ];

    if (additionalMenuPageObjectsArray.length > 0) {
        for (const additionalMenuPageObject of additionalMenuPageObjectsArray) {
            const { menuPageKey, menuPageObjects } = additionalMenuPageObject;

            menuPages.push({ pageKey: menuPageKey, content: menuPageObjects });
        }
    }

    return (
        <>
            {menuPages.map(({ pageKey, content }, index) => {
                const isDefault = menuPage === "default";
                const isVisible = menuPage === pageKey;
                const isSearch = menuPage === "search";

                return (
                    <React.Fragment key={index}>
                        <div
                            key={pageKey}
                            className="inner-mobile-dropdown-menu"
                            css={[
                                isDefault && firstOpen && fadeInCss,
                                isDefault && !firstOpen && slideInRightCss,
                                !isDefault && slideInLeftCss,
                                isVisible
                                    ? shownInnerMobileDropdownMenuStyle
                                    : hiddenInnerMobileDropdownMenuStyle
                            ]}
                        >
                            {content}
                        </div>

                        {/* Dropdowns for navigation items of products */}
                        <MobileProductPageNavDropdowns
                            productKey={productKey}
                            menuPage={menuPage}
                        />

                        {isSearch && (
                            <div
                                key={pageKey}
                                className="inner-mobile-dropdown-menu"
                                css={[
                                    slideInLeftCss,
                                    shownInnerMobileDropdownMenuStyle
                                ]}
                            >
                                <AutoCompleteSearchBar isMobileView={true} />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const defaultJiraLogo = (
    <Logo
        brand="jira"
        type="logo"
        appearance="brand"
        height={"25.6px"}
        width={"60px"}
    />
);

// eslint-disable-next-line complexity
export const MobileProductPageNav = ({
    productKey,
    mobileLogo = defaultJiraLogo,
    mobileLogoAriaLabel = "Jira logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "ProductPageNavJiraV2",
    mobileLinksSections,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = true,
    isShowTryNowLink = true,
    additionalMenuPageObjectsArray = [],
    isShowSearchButton = true,
    isShowAppSwitcher = true,
    isShowTabletDropdown = true,
    navType = "productNav"
}: MobileGlobalNavProps) => {
    const [isOpenSearch, setIsOpenSearch] = useState(false);

    const {
        userInfo,
        showMenu,
        isDisplayLoggedInUseDropdown,
        firstOpen,
        menuPage,
        handleToggleMenu,
        handleFirstOpen,
        handleMenuPage,
        handleClickAvatar
    } = useMobileNavDisplay(isOpenSearch);

    const {
        onSwitchAccountClick,
        onManageProfileClick,
        onManageLicensesClick,
        onLogOutClick
    } = useGlobalNavUserProfileClickActions({ email: userInfo.email });

    const mobileGlobalNavDropdownMenu = () => (
        <MobileNavProductPageDropdownMenu
            isUserLoggedIn={userInfo.isUserLoggedIn}
            menuPage={menuPage as DropdownType}
            firstOpen={firstOpen}
            handleMenuPage={handleMenuPage}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
            additionalMenuPageObjectsArray={additionalMenuPageObjectsArray}
            isShowSearchButton={isShowSearchButton}
        />
    );

    return (
        <div
            css={[
                containerCss,
                showMenu ? containerOpenCss : containerClosedCss
            ]}
        >
            <div css={[showMenu ? navBarOpenCss : navBarClosedCss]}>
                <MobileNavTop
                    productKey={productKey}
                    isLoggedIn={userInfo.isUserLoggedIn}
                    userName={userInfo.name ?? ""}
                    picture={userInfo.picture ?? ""}
                    handleClickAvatar={handleClickAvatar}
                    showMenu={showMenu}
                    handleToggleMenu={handleToggleMenu}
                    menuPage={menuPage as DropdownType}
                    handleMenuPage={handleMenuPage}
                    handleFirstOpen={handleFirstOpen}
                    mobileLogo={mobileLogo}
                    mobileLogoAriaLabel={mobileLogoAriaLabel}
                    mobileLogoHref={mobileLogoHref}
                    mobileLogoDataEventContainer={mobileLogoDataEventContainer}
                    isOpenSearch={isOpenSearch}
                    setIsOpenSearch={setIsOpenSearch}
                    tabletDropdownMenu={mobileGlobalNavDropdownMenu()}
                    isProductPage={true}
                    isShowTryNowLink={isShowTryNowLink}
                    navigationTrynowCtaText={ctaText}
                    navigationTrynowCtaUrl={ctaUrl}
                    navigationTrynowCtaOnClick={ctaOnClick}
                    isShowSearchButton={isShowSearchButton}
                    isShowAppSwitcher={isShowAppSwitcher}
                    isShowTabletDropdown={isShowTabletDropdown}
                    navType={navType}
                />
            </div>
            <div
                css={[menuContentStyle, showMenu && css({ display: "block" })]}
                id="nav-mobile-dropdown-menu"
            >
                <div>
                    <Divider />
                </div>
                {isDisplayLoggedInUseDropdown ? (
                    <MobileLoggedInUseDropdown
                        userInfo={{
                            displayName: userInfo.name || "",
                            email: userInfo.email || "",
                            avatar: userInfo.picture || ""
                        }}
                        onLogOutClick={onLogOutClick}
                        onManageLicensesClick={onManageLicensesClick}
                        onManageProfileClick={onManageProfileClick}
                        onSwitchAccountClick={onSwitchAccountClick}
                    />
                ) : (
                    mobileGlobalNavDropdownMenu()
                )}
            </div>
        </div>
    );
};
